import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { authService } from '../services/auth.service';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [token, setToken] = useState(
    searchParams.get('token') ||
      searchParams.get('codigo') ||
      searchParams.get('code') ||
      '',
  );
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Sincroniza query params se a rota for atualizada
  useEffect(() => {
    const emailParam = searchParams.get('email');
    const tokenParam =
      searchParams.get('token') ||
      searchParams.get('codigo') ||
      searchParams.get('code');

    if (emailParam) {
      setEmail(emailParam);
    }
    if (tokenParam) {
      setToken(tokenParam);
    }
  }, [searchParams]);

  // Permitir scroll nesta página (já que body e root têm overflow: hidden por padrão)
  useEffect(() => {
    const originalBodyOverflow = document.body.style.overflow;
    const originalRootOverflow = document.getElementById('root')?.style.overflow || '';
    const rootElement = document.getElementById('root');
    
    document.body.style.overflow = 'auto';
    if (rootElement) {
      rootElement.style.overflow = 'auto';
      rootElement.style.height = 'auto';
      rootElement.style.minHeight = '100vh';
    }

    return () => {
      document.body.style.overflow = originalBodyOverflow;
      if (rootElement) {
        rootElement.style.overflow = originalRootOverflow;
        rootElement.style.height = '';
        rootElement.style.minHeight = '';
      }
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const cleanEmail = email.trim().toLowerCase();
    const cleanToken = token.trim();

    // Validation
    if (!cleanEmail || !cleanEmail.includes('@')) {
      setError('Por favor, informe um email válido');
      setLoading(false);
      return;
    }

    if (cleanToken.length !== 6) {
      setError('O código de verificação deve ter exatamente 6 dígitos');
      setLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setError('A senha deve ter no mínimo 8 caracteres');
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('As senhas não coincidem');
      setLoading(false);
      return;
    }

    try {
      await authService.resetPassword(cleanEmail, cleanToken, newPassword);
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: unknown) {
      const errorObj = err as { response?: { data?: { message?: string } }; message?: string };
      setError(errorObj.response?.data?.message || errorObj?.message || 'Erro ao redefinir senha');
    } finally {
      setLoading(false);
    }
  };

  const isEmailReadOnly = Boolean(searchParams.get('email'));

  return (
    <div className="min-h-screen flex relative bg-green-900 lg:bg-white">
      {/* Background de Imagem para Mobile */}
      <div className="absolute inset-0 z-0 lg:hidden pointer-events-none">
         <div className="absolute inset-0 bg-green-950/80 mix-blend-multiply z-10" />
         <img src="/mapa_brasil.webp" alt="SST Exames" className="absolute inset-0 w-full h-full object-cover opacity-60 mix-blend-overlay" />
      </div>

      {/* Lado Esquerdo - Imagem/Banner (Oculto em mobile, 50% em desktop fixa) */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-green-900 overflow-hidden shadow-2xl z-10 flex-shrink-0">
         <div className="absolute inset-0 bg-green-900/60 mix-blend-multiply z-10" />
         <img src="/mapa_brasil.webp" alt="SST Exames Corporativo" className="absolute inset-0 w-full h-full object-cover opacity-60 mix-blend-overlay" />
         <div className="relative z-20 flex flex-col justify-center px-12 xl:px-24 text-white h-full w-full max-w-3xl">
            <img src="/sst_logo_negativo.svg" alt="SST Exames" className="h-16 w-auto object-contain mb-10 origin-left transform scale-100 self-start" />
            <div className="pt-8 lg:pt-0">
              <h1 className="text-4xl lg:text-5xl font-bold tracking-tight drop-shadow-lg lg:drop-shadow-none">
                Portal do Cliente
              </h1>
            </div>
            {/* Rodapé da imagem (desktop) */}
            <div className="hidden lg:block absolute bottom-12 left-12 xl:left-24">
              <p className="text-sm opacity-75">
                &copy; {new Date().getFullYear()} SST Exames. Todos os direitos reservados.
              </p>
            </div>
         </div>
      </div>

      {/* Lado Direito - Formulário */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20 xl:px-24 bg-transparent lg:bg-gray-50 relative z-20">
        <div className="mx-auto w-full max-w-sm lg:max-w-md">
          <div className="lg:hidden mb-10 flex justify-center drop-shadow-lg">
             <img src="/sst_logo_negativo.svg" alt="SST Exames" className="h-14 w-auto" />
          </div>

          <div className="mb-10 text-center">
            <h2 className="text-3xl font-extrabold tracking-tight text-white lg:text-green-700">
              Redefinir Senha
            </h2>
          </div>

          <div className="bg-white py-8 px-6 shadow-2xl shadow-black/40 lg:shadow-gray-200/50 sm:rounded-xl sm:px-10 border-0 lg:border lg:border-gray-100">
            {success ? (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-md p-4 text-center">
                  <div className="text-sm text-green-700">
                    <p className="font-medium">Senha redefinida com sucesso!</p>
                    <p className="mt-2">Redirecionando para o login...</p>
                  </div>
                </div>
              </div>
            ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 text-left">
                  Email
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    readOnly={isEmailReadOnly}
                    required
                    placeholder="seu@email.com"
                    className={`appearance-none block w-full px-4 py-3 border-0 rounded-xl shadow-inner-sm text-gray-900 placeholder-gray-400 focus:outline-none transition-all duration-200 ${
                      isEmailReadOnly ? 'bg-gray-50 text-gray-600' : 'bg-white border border-gray-300 focus:ring-2 focus:ring-green-500'
                    }`}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="token" className="block text-sm font-medium text-gray-700 text-left">
                  Código de Verificação (6 dígitos)
                </label>
                <div className="mt-1">
                  <input
                    id="token"
                    name="token"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={token}
                    onChange={(e) => setToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    required
                    className="appearance-none block w-full px-4 py-3 border border-gray-300 bg-white rounded-xl shadow-inner-sm text-center text-2xl tracking-widest font-mono text-gray-900 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all duration-200"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500 text-center">
                  Insira o código de 6 números enviado para o seu e-mail
                </p>
              </div>

              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 text-left">
                  Nova Senha
                </label>
                <div className="mt-1">
                  <input
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={8}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="appearance-none block w-full px-4 py-3 border border-gray-300 bg-white rounded-xl shadow-inner-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all duration-200"
                    placeholder="Mínimo 8 caracteres"
                    disabled={loading}
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500 text-center">
                  A senha deve ter no mínimo 8 caracteres
                </p>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 text-left">
                  Confirmar Senha
                </label>
                <div className="mt-1">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={8}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="appearance-none block w-full px-4 py-3 border border-gray-300 bg-white rounded-xl shadow-inner-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all duration-200"
                    placeholder="Digite a senha novamente"
                    disabled={loading}
                  />
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="text-sm text-red-700 text-center">{error}</div>
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={loading || !newPassword || !confirmPassword || !token || !email}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-lg shadow-green-600/20 text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02] items-center"
                >
                  {loading && (
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  )}
                  {loading ? 'Redefinindo...' : 'Redefinir Senha'}
                </button>
              </div>

              <div className="text-center">
                <Link
                  to="/login"
                  className="text-sm font-medium text-gray-600 hover:text-gray-900"
                >
                  Voltar para o login
                </Link>
              </div>
            </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
